import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Camera, MapPin, Upload, X, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { ProjectCombobox } from '@/components/dashboard/ProjectCombobox';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ProjectProgressReporter({ projects, todaySchedule, currentLocation }) {
  const [showDialog, setShowDialog] = useState(false);
  const [categories, setCategories] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [form, setForm] = useState({
    project_id: '',
    floor: '',
    unit_no: '',
    work_status: '',
    notes: '',
    photos: []
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (todaySchedule?.project_id) {
      setForm(prev => ({ ...prev, project_id: todaySchedule.project_id }));
    }
  }, [todaySchedule]);

  const fetchCategories = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/company/progress-categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCategories(data.categories || []);
      }
    } catch (e) {}
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    const token = localStorage.getItem('token');
    const newPhotos = [];

    for (const file of files) {
      if (file.size > 20 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Max 20MB.`);
        continue;
      }
      const formData = new FormData();
      formData.append('files', file);
      try {
        const res = await fetch(`${API_URL}/api/upload-images`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        if (res.ok) {
          const data = await res.json();
          if (data.urls?.length > 0) {
            newPhotos.push({
              url: data.urls[0],
              name: file.name,
              location: currentLocation ? { lat: currentLocation.latitude, lng: currentLocation.longitude, address: currentLocation.address } : null
            });
          }
        }
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    setForm(prev => ({ ...prev, photos: [...prev.photos, ...newPhotos] }));
    setUploading(false);
    if (newPhotos.length > 0) toast.success(`${newPhotos.length} photo(s) uploaded`);
    e.target.value = '';
  };

  const removePhoto = (idx) => {
    setForm(prev => ({ ...prev, photos: prev.photos.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async () => {
    if (!form.project_id || !form.work_status) {
      toast.error('Please select a project and work status');
      return;
    }
    setSubmitting(true);
    const token = localStorage.getItem('token');
    try {
      const payload = {
        ...form,
        location: currentLocation ? { lat: currentLocation.latitude, lng: currentLocation.longitude, address: currentLocation.address } : null
      };
      const res = await fetch(`${API_URL}/api/projects/${form.project_id}/progress`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        toast.success('Progress reported successfully');
        setShowDialog(false);
        setWizardStep(1);
        setForm({ project_id: todaySchedule?.project_id || '', floor: '', unit_no: '', work_status: '', notes: '', photos: [] });
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to report progress');
      }
    } catch (e) {
      toast.error('Connection error');
    }
    setSubmitting(false);
  };

  const selectedProject = projects?.find(p => p.id === form.project_id);

  return (
    <>
      <Button
        onClick={() => setShowDialog(true)}
        className="action-btn action-btn-secondary"
        size="lg"
        variant="outline"
        data-testid="report-progress-btn"
      >
        <Camera className="w-5 h-5 mr-2" />
        Report Project Progress
      </Button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              Report Project Progress
            </DialogTitle>
          </DialogHeader>

          {/* Progress Bar */}
          <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
            {[{ step: 1, label: 'Details' }, { step: 2, label: 'Photos' }, { step: 3, label: 'Review' }].map(({ step, label }) => (
              <button key={step} onClick={() => setWizardStep(step)} style={{
                flex: 1, padding: '8px 4px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                background: wizardStep === step ? '#5b63d3' : wizardStep > step ? '#16a34a' : '#e2e8f0',
                color: wizardStep >= step ? 'white' : '#64748b', fontSize: '0.75rem', fontWeight: 600
              }}>
                {step}. {label}
              </button>
            ))}
          </div>

          {/* Step 1: Details */}
          {wizardStep === 1 && (
            <div className="space-y-4">
              <div>
                <Label>Project *</Label>
                <ProjectCombobox
                  projects={projects || []}
                  value={form.project_id}
                  onChange={(val) => setForm({ ...form, project_id: val })}
                  placeholder="Select project"
                  filterActive={false}
                  testId="progress-report-project"
                />
              </div>
              <div>
                <Label>Work Status *</Label>
                <Select value={form.work_status} onValueChange={(val) => setForm({ ...form, work_status: val })}>
                  <SelectTrigger><SelectValue placeholder="Select status" /></SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Floor</Label>
                  <Input value={form.floor} onChange={(e) => setForm({ ...form, floor: e.target.value })} placeholder="e.g. 3rd Floor" />
                </div>
                <div>
                  <Label>Unit No. / Location Description</Label>
                  <Input value={form.unit_no} onChange={(e) => setForm({ ...form, unit_no: e.target.value })} placeholder="e.g. Unit 301, Corridor, Lobby" />
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Additional details..." rows={3} />
              </div>
              {currentLocation && (
                <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-50 p-2 rounded">
                  <MapPin className="w-3 h-3" />
                  <span>{currentLocation.address}</span>
                </div>
              )}
              <div className="flex justify-end">
                <Button onClick={() => setWizardStep(2)} disabled={!form.project_id || !form.work_status}>
                  Next: Add Photos →
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Photos */}
          {wizardStep === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">Take or upload photos of the work completed. Each photo is tagged with your current location.</p>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                <input type="file" id="progress-photo-upload" multiple accept="image/*" capture="environment"
                  onChange={handlePhotoUpload} style={{ display: 'none' }} />
                <label htmlFor="progress-photo-upload" style={{ cursor: 'pointer' }}>
                  <Camera className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm font-medium">Tap to take photo or upload</p>
                  <p className="text-xs text-gray-500 mt-1">Max 20MB per photo</p>
                </label>
              </div>
              {uploading && <p className="text-sm text-blue-600 text-center">Uploading...</p>}

              {form.photos.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {form.photos.map((photo, idx) => (
                    <div key={idx} className="relative">
                      <img src={`${API_URL}${photo.url}`} alt={photo.name} className="w-full h-24 object-cover rounded-lg border" />
                      <button onClick={() => removePhoto(idx)}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center">
                        <X className="w-3 h-3" />
                      </button>
                      {photo.location && (
                        <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[8px] px-1 py-0.5 rounded-b-lg truncate">
                          <MapPin className="w-2 h-2 inline mr-0.5" />{photo.location.address}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setWizardStep(1)}>← Back</Button>
                <Button onClick={() => setWizardStep(3)}>
                  Review →
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Review & Submit */}
          {wizardStep === 3 && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4 border space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Project:</span>
                  <span className="text-sm font-semibold">{selectedProject?.name || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Status:</span>
                  <span className="text-sm font-semibold text-purple-600">{form.work_status}</span>
                </div>
                {form.floor && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Floor:</span>
                    <span className="text-sm">{form.floor}</span>
                  </div>
                )}
                {form.unit_no && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Unit:</span>
                    <span className="text-sm">{form.unit_no}</span>
                  </div>
                )}
                {form.notes && (
                  <div>
                    <span className="text-sm text-gray-500">Notes:</span>
                    <p className="text-sm mt-1">{form.notes}</p>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Photos:</span>
                  <span className="text-sm">{form.photos.length}</span>
                </div>
                <Button variant="link" size="sm" onClick={() => setWizardStep(1)} className="p-0 h-auto text-xs">Edit Details</Button>
              </div>

              {form.photos.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {form.photos.map((photo, idx) => (
                    <img key={idx} src={`${API_URL}${photo.url}`} alt={photo.name} className="w-16 h-16 object-cover rounded border" />
                  ))}
                </div>
              )}

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setWizardStep(2)}>← Back</Button>
                <Button onClick={handleSubmit} disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Progress'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ProjectProgressReporter;
